
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AddressAutocomplete } from "@/components/properties/AddressAutocomplete";
import { cn } from "@/lib/utils";
import { FieldError } from "@/components/properties/FieldError";

interface PropertyLocationFormProps {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errors?: Record<string, string>;
  clearFieldError?: (field: string) => void;
}

export const PropertyLocationForm = ({
  address,
  city,
  state,
  zipCode,
  onChange,
  errors = {},
  clearFieldError,
}: PropertyLocationFormProps) => {
  const errCls = (field: string) =>
    errors[field] ? "border-destructive focus-visible:ring-destructive" : "";

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Property Location</h2>
          <Separator />
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address">Street Address*</Label>
              <AddressAutocomplete
                initialAddress={address}
                onAddressSelect={(addressData) => {
                  const syntheticEvent = {
                    target: { name: 'address', value: addressData.address }
                  } as React.ChangeEvent<HTMLInputElement>;
                  onChange(syntheticEvent);
                  
                  const cityEvent = { target: { name: 'city', value: addressData.city } } as React.ChangeEvent<HTMLInputElement>;
                  const stateEvent = { target: { name: 'state', value: addressData.state } } as React.ChangeEvent<HTMLInputElement>;
                  const zipEvent = { target: { name: 'zipCode', value: addressData.zipCode } } as React.ChangeEvent<HTMLInputElement>;
                  
                  onChange(cityEvent);
                  onChange(stateEvent);
                  onChange(zipEvent);
                  clearFieldError?.("address");
                  clearFieldError?.("city");
                  clearFieldError?.("state");
                  clearFieldError?.("zipCode");
                }}
                onInputChange={(value) => {
                  const syntheticEvent = {
                    target: { name: 'address', value }
                  } as React.ChangeEvent<HTMLInputElement>;
                  onChange(syntheticEvent);
                }}
                className={cn("bg-muted", errCls("address"))}
                showLabel={false}
              />
              <FieldError message={errors.address} />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="city">City*</Label>
              <Input 
                id="city" 
                name="city"
                value={city}
                onChange={onChange}
                required 
                aria-invalid={!!errors.city}
                className={cn("bg-muted", errCls("city"))} 
              />
              <FieldError message={errors.city} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State*</Label>
              <Input 
                id="state" 
                name="state"
                value={state}
                onChange={onChange}
                required 
                aria-invalid={!!errors.state}
                className={cn("bg-muted", errCls("state"))} 
              />
              <FieldError message={errors.state} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zipCode">Zip Code*</Label>
              <Input 
                id="zipCode" 
                name="zipCode"
                value={zipCode}
                onChange={onChange}
                required 
                aria-invalid={!!errors.zipCode}
                className={cn("bg-muted", errCls("zipCode"))} 
              />
              <FieldError message={errors.zipCode} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
